// ErrorBoundaryProvider.tsx
'use client';

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const NiceErrorMessageWithStack = ({
    error,
    errorInfo,
}: {
    error: Error | null;
    errorInfo: { componentStack: string | undefined } | null;
}) => {
    if (!error || !errorInfo) {
        return <h1>Something went wrong</h1>;
    }
    return (
        <div className="fireknife-error-boundary">
            <style>
                {`
                    .fireknife-error-boundary {
                        background-color: #f8d7da;
                        color: #721c24;
                        padding: 20px;
                        border-radius: 5px;
                        margin: 20px;
                        font-family: Arial, sans-serif;
                    }
                    .fireknife-error-boundary h1 {
                        font-size: 16px !important;
                        font-weight: bold;
                    }
                    .fireknife-error-boundary h2 {
                        font-size: 14px !important;
                        margin-block-start: 20px;
                        margin-block-end: 10px;
                    }
                    .fireknife-error-boundary pre {
                        background-color: #f1f1f1;
                        padding: 10px;
                        border-radius: 5px;
                        overflow-x: auto;
                        white-space: pre-wrap;
                        word-wrap: break-word;
                        font-family: 'Courier New', Courier, monospace;
                        font-size: 12px !important;
                        color: #333;
                    }
                `}
            </style>
            <h1 className="fireknife-error-message">{error.message}</h1>
            <h2>Component stack:</h2>
            <pre>{errorInfo.componentStack}</pre>
            <h2>Stack trace:</h2>
            {error.stack && <pre>{error.stack}</pre>}
        </div>
    );
};

type ErrorBoundaryContextValue = {
    error: Error | null;
    errorInfo: { componentStack: string } | null;
};

const ErrorBoundaryContext = createContext<ErrorBoundaryContextValue | null>(null);

export const ErrorBoundaryProvider = ({ children }: { children: ReactNode }) => {
    const [error, setError] = useState<Error | null>(null);
    const [errorInfo, setErrorInfo] = useState<{ componentStack: string } | null>(null);

    const onError = useCallback((err: Error, info: { componentStack: string }) => {
        setError(err);
        setErrorInfo(info);
    }, []);

    useEffect(() => {
        if (import.meta?.hot) {
            import.meta.hot.accept(() => {
                setError(null);
                setErrorInfo(null);
            });
        }
    }, []);

    return (
        <ErrorBoundary
            fallback={<NiceErrorMessageWithStack error={error} errorInfo={errorInfo} />}
            onReset={() => {
                setError(null);
                setErrorInfo(null);
            }}
            onError={onError}
        >
            <ErrorBoundaryContext.Provider value={{ error, errorInfo }}>
                {children}
            </ErrorBoundaryContext.Provider>
        </ErrorBoundary>
    );
};

export const useErrorBoundary = () => {
    const context = useContext(ErrorBoundaryContext);
    if (!context) {
        throw new Error('useErrorBoundary must be used within an ErrorBoundaryProvider');
    }
    return context;
};
